<template>
	<div class="section">
		<div class="container">
			<h1>Frequently Asked Questions</h1>

			<ol>
				<li><a href="#ctc">What is the Cape Town Convention?</a></li>
				<li><a href="#index">What is the Cape Town Convention Compliance Index (the Index)?</a></li>
				<li><a href="#use">What is the intended use of the Index?</a></li>
				<li><a href="#updated">How often is the Index updated?</a></li>
				<li>
					<a href="#watchlist">
						What if an event relating to CTC compliance occurs in between regular updates (such as a
						court decision refusing CTC remedies, or insolvency proceedings are initiated that
						implicates the insolvency rules under Alternative A)?
					</a>
				</li>
				<li><a href="#qualifying-declarations">What are the 'qualifying declarations'?</a></li>
				<li><a href="#access">How can I access proprietary information?</a></li>
				<li>
					<a href="#cft">What is a 'certified-for-transaction' scorecard and how is it used?</a>
				</li>
				<li><a href="#report">How can I report a CTC-related experience or issue to AWG?</a></li>
				<li>
					<a href="#governments">
						Does AWG work with governments of the contracting states to improve CTC compliance and,
						thus, Index scores?
					</a>
				</li>
			</ol>
			<hr />
			<h3 id="ctc">1. What is the Cape Town Convention?</h3>
			<p>
				The Convention on International Interests in Mobile Equipment was signed on 16 November 2001
				and has become, together with the associated Protocol on Matters Specific to Aircraft
				Equipment (collectively, 'CTC'), the most successful international treaty in the field of
				private law.
			</p>
			<p>
				CTC is designed to facilitate asset-based financing and leasing of aviation equipment,
				expand financing opportunities, and reduce costs - thereby providing substantial economic
				benefits. It does so by reducing a creditor's risk and by enhancing legal predictability in
				these transactions, including in the case of a debtor's insolvency or other default. Such
				benefits and risk reduction presuppose compliance with CTC by the applicable contracting
				state.
			</p>
			<h3 id="index">2. What is the Cape Town Convention Compliance Index (the Index)?</h3>
			<p>
				The Index is prepared by AWG to monitor and assess compliance by contracting states with
				their undertakings under CTC. The Index will take into account many factors, detailed in the
				<a @click="$router.push('/about/methodology')">methodology</a>
				, and will provide a predictive assessment of a contracting state's anticipated future
				compliance. Several of AWG's historical projects reporting on implementation will be merged
				into this project, including its past work on summaries of national implementation for
				contracting states. A parallel and equally significant goal of the Index is to incentivize
				future compliance by providing accurate, timely information to stakeholders, including the
				OECD, and communicating concrete proposals for improving compliance in the applicable
				contracting state.
			</p>
			<h3 id="use">3. What is the intended use of the Index?</h3>
			<p>
				AWG developed the Index as a way of tracking and providing information on the level of
				compliance with CTC in individual jurisdictions to stakeholders. There are many potential
				uses for the Index, including in commercial transactions as a general risk assessment tool,
				in governmental efforts to improve CTC compliance, and in academic research.
			</p>
			<h3 id="updated">4. How often is the Index updated?</h3>
			<p>
				The Index will be updated regularly at semi-annual intervals, in March and September of each
				year.
			</p>
			<h3 id="watchlist">
				5. What if an event relating to CTC compliance occurs in between regular updates (such as a
				court decision refusing CTC remedies, or insolvency proceedings are initiated that
				implicates the insolvency rules under Alternative A)?
			</h3>
			<p>
				The Index will be kept current between semi-annual updates to reflect material developments
				that may increase or decrease scores, based on positive or negative state action (against
				the primacy and completeness standards). Such material developments result in a contracting
				state being placed on a CTC Compliance Watchlist, indicating that AWG has become aware of
				material developments that may affect the score but has not yet completed its assessment of
				the impact of such material developments. Once AWG has sufficient information to determine
				the impact on scoring of the relevant material developments such that a scoring adjustment
				is warranted (or deemed to be unnecessary), the applicable contracting state will come off
				the CTC Compliance Watchlist with its adjusted, or confirmed, score.
			</p>
			<h3 id="qualifying-declarations">6. What are the 'qualifying declarations'?</h3>
			<p>
				Certain declaration under the Convention and Protocol are specifically designed to reduce
				transaction risk, and, thus, produce economic benefits. These declarations are identified in
				the
				<a
					href="http://www.oecd.org/officialdocuments/publicdisplaydocumentpdf/?doclanguage=en&cote=tad/pg(2019)1">
					OECD Aircraft Sector Understanding (ASU) as qualifying declarations (see page 79)
				</a>
				.
			</p>
			<h3 id="access">7. How can I access proprietary information?</h3>
			<p>
				Any person may gain access to all or parts of the Index, including scorecards, annotations,
				CTC Compliance Watchlist notices, unlimited complimentary 'certified-for-transaction' (CFT)
				scorecards, and data-graphing capabilities at no cost by providing an email address and
				agreeing to the Terms and Conditions (please see Section 4 thereof for the privacy policy in
				connection with your usage of the Index website).
			</p>

			<p>
				Materials accessed may not be shared with any other party without AWG&#39;s written consent,
				with a limited exception for the CFT scorecard (please refer to the Terms and Conditions for
				further details).
			</p>
			<h3 id="cft">8. What is a 'certified-for-transaction' scorecard and how is it used?</h3>
			<p>
				A 'certified-for-transaction' (CFT) scorecard certifies, as of the date it is ordered, that
				the scorecard it attaches for a specific jurisdiction is the most up-to-date scorecard
				available. The CFT scorecard should be used in transaction closings as an indication and
				baseline for CTC compliance expectations in any particular contracting state. The CFT
				scorecard is a limited exception to the general restrictions on transfer and sharing of
				proprietary Index materials as it may be shared with parties to a transaction parties solely
				in connection with such transaction. Per our disclaimer
				<a @click="openTC">(Terms and Conditions)</a>
				, however, no part of the Index may be construed or relied on as legal advice.
			</p>
			<h3 id="report">9. How can I report a CTC-related experience or issue to AWG?</h3>
			<p>
				Please use the
				<router-link to="/contact/compliance">Report Compliance Issue page</router-link>
				to report any CTC-related experience or issues to AWG. AWG reviews all such reports in
				detail and will follow up, as needed.
			</p>
			<h3 id="governments">
				10. Does AWG work with governments of the contracting states to improve CTC compliance and,
				thus, Index scores?
			</h3>
			<p>
				Yes, AWG seeks to actively engage with government of contracting states to consult on CTC
				compliance matters, and has a long history of successfully doing so. AWG provides
				contracting states that have designated a communications channel with AWG for the purposes
				of the Index with a shadow scorecard, showing the potential effect on scoring of proposed
				practical actions to improve compliance in the applicable contracting state. These shadow
				scorecards are provided exclusively to the respective communications channels. AWG's
				informational guide to shadow scorecards, and how to read them, is available
				<a target="_blank" href="/AWG_CTC_Compliance_Index_-_Reading_the_Shadow_Scorecard.pdf">
					here
				</a>
				.
			</p>
		</div>
	</div>
</template>

<script>
	import termsAndConditions from "@/components/modals/termsAndConditions.vue";
	export default {
		created() {},
		mounted() {
			const anchor = this.$router.currentRoute.hash;
			this.$nextTick(() => {
				if (anchor && document.querySelector(anchor)) {
					location.href = anchor;
				}
			});
		},
		methods: {
			openPdf() {
				var pdf = "@/src/assets/pdfs/AWG_CTC_Compliance_Index_-_Reading_the_Shadow_Scorecard.pdf";
				window.open(pdf);
			},
			openTC() {
				this.$buefy.modal.open({
					parent: this,
					component: termsAndConditions,
					hasModalCard: true
				});
			}
		}
	};
</script>
